import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { radius, tokens } from "../theme/theme";

/**
 * The one confirmation dialog the panel asks its yes/no questions through.
 *
 * The three it replaced were copies of each other: a raw `Modal` holding a
 * 200px-wide absolutely-positioned white box with «Ні» and «Так» side by side
 * as identical outlined buttons. Two problems with that, beyond the look. The
 * box was 200px wide whatever it held, so the archive dialog's date field ran
 * out of room; and «Ні»/«Так» gave no clue which of the two was about to erase
 * something — the button that deletes a listing forever looked exactly like the
 * button that puts one back.
 *
 * So: the confirm button is labelled with the action rather than "yes", and
 * `tone` colours it — red for what cannot be undone, amber for what can,
 * green for what restores. Cancel is a quiet text button, and it is the one
 * that gets focus, so a stray Enter never confirms.
 *
 * @param tone  "danger" | "warning" | "positive"
 */

const TONES = {
  danger: { main: tokens.red600, soft: tokens.red100 },
  warning: { main: tokens.sand600, soft: tokens.sand100 },
  positive: { main: tokens.green600, soft: tokens.green100 },
};

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  tone,
  icon,
  title,
  description,
  confirmLabel,
  confirmIcon,
  cancelLabel,
  confirmDisabled,
  children,
}) {
  const palette = TONES[tone] || TONES.warning;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: radius.lg } }}
    >
      <DialogContent sx={{ pt: 3.5, pb: 2.5, px: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: palette.soft,
              color: palette.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
            {description ? (
              <Typography
                variant="body2"
                sx={{ color: tokens.ink500, mt: 0.75 }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {children ? <Box sx={{ mt: 3 }}>{children}</Box> : null}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid " + tokens.line, gap: 1 }}
      >
        {/* Deliberately no autoFocus: MUI focuses the dialog itself, so Enter
            confirms nothing, and focusing Cancel would paint a permanent focus
            ring on the quiet button every time the dialog opens. */}
        <Button variant="text" onClick={onClose}>
          {cancelLabel || "Скасувати"}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!!confirmDisabled}
          startIcon={confirmIcon}
          onClick={onConfirm}
          sx={{
            backgroundColor: palette.main,
            "&:hover": { backgroundColor: palette.main, filter: "brightness(0.92)" },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
